import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTablePageEvent,
  DataTableRowClickEvent,
} from 'primereact/datatable';
import { AccountingJournalReportDetailsEntity } from '../../../../api/types/accounting/journal-entries/accounting-journal-report-details-entity';
import { formatDate } from '../../../../utils/date-format';
import { downloadJournalEntryReportDatOperation } from '../operations/download-journal-entry-report-dat-operation';
import { exportJournalEntryReportFlatDataExcelOperation } from '../operations/export-journal-entry-report-flat-data-excel-operation';
import { exportJournalEntryReportFlatDataPdfOperation } from '../operations/export-journal-entry-report-flat-data-pdf-operation';
import { getJournalEntryReportsListOperation } from '../operations/get-journal-entry-reports-list-operation';
import { JournalEntryReportDetailsDialog } from './components/journal-entry-report-details-dialog';
import { JournalEntriesReportsFilter } from './components/journal-entries-reports-filter';
import {
  JournalEntryReportsFilterType,
  defaultJournalEntryReportsFilter,
} from './journal-entries-reports-filter.type';

const DEFAULT_OFFSET = 0;
const DEFAULT_LIMIT = 20;

export const JournalEntriesReportsPage = () => {
  const [data, setData] = useState<AccountingJournalReportDetailsEntity[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const [offset, setOffset] = useState<number>(DEFAULT_OFFSET);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);

  const [filters, setFilters] = useState<JournalEntryReportsFilterType>(
    defaultJournalEntryReportsFilter(),
  );

  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [detailsDialogVisible, setDetailsDialogVisible] =
    useState<boolean>(false);

  // Per-row export loading — keyed by report id so only the clicked row spins.
  const [exportingPdfReportId, setExportingPdfReportId] = useState<
    number | null
  >(null);
  const [exportingExcelReportId, setExportingExcelReportId] = useState<
    number | null
  >(null);
  const [downloadingDatReportId, setDownloadingDatReportId] = useState<
    number | null
  >(null);

  const onRowClick = (event: DataTableRowClickEvent) => {
    const row = event.data as AccountingJournalReportDetailsEntity;
    if (!row || typeof row.id !== 'number') return;
    setSelectedReportId(row.id);
    setDetailsDialogVisible(true);
  };

  const onDetailsDialogHide = () => {
    setDetailsDialogVisible(false);
    setSelectedReportId(null);
  };

  const anyExportInFlight =
    exportingPdfReportId !== null ||
    exportingExcelReportId !== null ||
    downloadingDatReportId !== null;

  const handleExportPdf = (e: React.MouseEvent, reportId: number) => {
    e.stopPropagation();
    if (anyExportInFlight) return;
    exportJournalEntryReportFlatDataPdfOperation(reportId, (isLoading) => {
      setExportingPdfReportId(isLoading ? reportId : null);
    });
  };

  const handleExportExcel = (e: React.MouseEvent, reportId: number) => {
    e.stopPropagation();
    if (anyExportInFlight) return;
    exportJournalEntryReportFlatDataExcelOperation(reportId, (isLoading) => {
      setExportingExcelReportId(isLoading ? reportId : null);
    });
  };

  const handleDownloadDat = (e: React.MouseEvent, reportId: number) => {
    e.stopPropagation();
    if (anyExportInFlight) return;
    downloadJournalEntryReportDatOperation(reportId, (isLoading) => {
      setDownloadingDatReportId(isLoading ? reportId : null);
    });
  };

  const actionsBody = (row: AccountingJournalReportDetailsEntity) => {
    const reportId = row.id;
    const isExportingPdf = exportingPdfReportId === reportId;
    const isExportingExcel = exportingExcelReportId === reportId;
    const isDownloadingDat = downloadingDatReportId === reportId;

    return (
      <div
        style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          icon="pi pi-file-pdf"
          tooltip="ייצוא לPDF"
          tooltipOptions={{ position: 'top', showOnDisabled: true }}
          onClick={(e) => handleExportPdf(e, reportId)}
          loading={isExportingPdf}
          disabled={anyExportInFlight && !isExportingPdf}
          style={{
            backgroundColor: '#dc2626',
            borderColor: '#dc2626',
            height: '32px',
            width: '48px',
          }}
        />
        <Button
          icon="pi pi-file-excel"
          tooltip="ייצוא לאקסל"
          tooltipOptions={{ position: 'top', showOnDisabled: true }}
          onClick={(e) => handleExportExcel(e, reportId)}
          loading={isExportingExcel}
          disabled={anyExportInFlight && !isExportingExcel}
          style={{
            backgroundColor: '#10b981',
            borderColor: '#10b981',
            height: '32px',
            width: '48px',
          }}
        />
        <Button
          icon="pi pi-download"
          tooltip="הורדת קובץ לחשבשבת"
          tooltipOptions={{ position: 'top', showOnDisabled: true }}
          onClick={(e) => handleDownloadDat(e, reportId)}
          loading={isDownloadingDat}
          disabled={anyExportInFlight && !isDownloadingDat}
          style={{
            backgroundColor: '#2563eb',
            borderColor: '#2563eb',
            height: '32px',
            width: '48px',
          }}
        />
      </div>
    );
  };

  const runSearch = async (
    nextFilters: JournalEntryReportsFilterType,
    nextOffset: number,
    nextLimit: number,
  ) => {
    await getJournalEntryReportsListOperation(
      {
        offset: nextOffset,
        limit: nextLimit,
        startDateFrom: nextFilters.startDateFrom,
        endDateFrom: nextFilters.endDateFrom,
      },
      setData,
      setTotal,
      setLoading,
    );
  };

  useEffect(() => {
    runSearch(filters, offset, limit);
  }, []);

  const handleSearch = async () => {
    setOffset(DEFAULT_OFFSET);
    await runSearch(filters, DEFAULT_OFFSET, limit);
  };

  const handleClear = () => {
    setFilters(defaultJournalEntryReportsFilter());
  };

  const onPageChange = (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    runSearch(filters, newOffset, newLimit);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '12px',
        direction: 'rtl',
      }}
    >
      <JournalEntriesReportsFilter
        filters={filters}
        loading={loading}
        onChange={setFilters}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <div style={{ fontSize: '14px', color: '#4b5563', textAlign: 'right' }}>
          סה״כ דוחות: {total.toLocaleString()}
        </div>

        <DataTable
          value={data}
          rowHover
          loading={loading}
          paginator
          rows={limit}
          first={offset}
          totalRecords={total}
          onPage={onPageChange}
          lazy={true}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו דוחות</div>
          }
          dataKey="id"
          tableStyle={{ direction: 'rtl' }}
          onRowClick={onRowClick}
          rowClassName={() => 'cursor-pointer'}
        >
          <Column
            field="id"
            header="מזהה"
            style={{ textAlign: 'right', width: '90px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="entriesCount"
            header="מס׳ פקודות יומן"
            style={{ textAlign: 'right', width: '160px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingJournalReportDetailsEntity) =>
              typeof row.entriesCount === 'number'
                ? row.entriesCount.toLocaleString()
                : 'חסר'
            }
          />
          <Column
            field="linesCount"
            header="מס׳ פעולות"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingJournalReportDetailsEntity) =>
              typeof row.linesCount === 'number'
                ? row.linesCount.toLocaleString()
                : 'חסר'
            }
          />
          <Column
            field="createdAt"
            header="תאריך יצירה"
            style={{ textAlign: 'right', width: '160px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingJournalReportDetailsEntity) =>
              row.createdAt ? formatDate(row.createdAt as Date) : 'חסר'
            }
          />
          <Column
            field="createdByName"
            header="נוצר על ידי"
            style={{ textAlign: 'right', width: '180px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingJournalReportDetailsEntity) =>
              row.createdByName?.trim() ? row.createdByName : 'חסר'
            }
          />
          <Column
            header=""
            style={{ textAlign: 'right', width: '180px' }}
            headerStyle={{ textAlign: 'right' }}
            body={actionsBody}
          />
        </DataTable>
      </div>

      <JournalEntryReportDetailsDialog
        visible={detailsDialogVisible}
        reportId={selectedReportId}
        onHide={onDetailsDialogHide}
      />
    </div>
  );
};
